import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import UnifiedIcon from '../../../components/UnifiedIcon';
import IconHoverText from '../../../components/icon-hover-text/IconHoverText';

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function RingGauge({ percent = 0, colorHex = '#14b8a6' }) {
  const pct = clamp(typeof percent === 'number' ? percent : 0, 0, 100);
  const style = useMemo(
    () => ({
      background: `conic-gradient(${colorHex} ${pct * 3.6}deg, #e5e7eb 0deg)`,
    }),
    [pct, colorHex]
  );

  return (
    <div className="flex items-center justify-center">
      <div className="relative h-44 w-44 rounded-full" style={style}>
        <div className="absolute inset-3 rounded-full bg-white flex flex-col items-center justify-center">
          <div className="text-2xl font-semibold text-gray-900">{pct} / 100</div>
        </div>
      </div>
    </div>
  );
}

export default function TrustCenterSummary({
  isLoading,
  readiness,
  summary,
  colorHex,
  cultureReadiness,
  cultureStatement,
  isCultureStatementLoading,
}) {
  const { t } = useTranslation(['trust-center']);

  const modules = Array.isArray(cultureReadiness?.modules) ? cultureReadiness.modules : [];

  const infraPercent =
    cultureReadiness && typeof cultureReadiness.availabilityPercent === 'number'
      ? clamp(cultureReadiness.availabilityPercent, 0, 100)
      : null;
  const adoptionPercent =
    cultureReadiness && typeof cultureReadiness.adoptionPercent === 'number'
      ? clamp(cultureReadiness.adoptionPercent, 0, 100)
      : null;
  const engagementPercent =
    cultureReadiness && typeof cultureReadiness.engagementPercent === 'number'
      ? clamp(cultureReadiness.engagementPercent, 0, 100)
      : null;

  const infraWeight = 20;
  const adoptionWeight = 30;
  const engagementWeight = 50;

  const [showInfraDetails, setShowInfraDetails] = useState(false);
  const [showAdoptionDetails, setShowAdoptionDetails] = useState(false);
  const [showEngagementDetails, setShowEngagementDetails] = useState(false);

  const MODULE_COPY = {
    pulse: {
      infra: t('trust-center.kci.infrastructure.pulse'),
      adoption: t('trust-center.kci.adoption.pulse'),
    },
    recognition: {
      infra: t('trust-center.kci.infrastructure.recognition'),
      adoption: t('trust-center.kci.adoption.recognition'),
    },
    performancereview: {
      infra: t('trust-center.kci.infrastructure.performancereview'),
      adoption: t('trust-center.kci.adoption.performancereview'),
    },
    calibration: {
      infra: t('trust-center.kci.infrastructure.calibration'),
      adoption: t('trust-center.kci.adoption.calibration'),
    },
    '1on1s': {
      infra: t('trust-center.kci.infrastructure.1on1s'),
      adoption: t('trust-center.kci.adoption.1on1s'),
    },
  };

  const stageKey = (() => {
    if (typeof readiness !== 'number') return 'unknown';
    if (readiness < 40) return 'reactive';
    if (readiness < 55) return 'listening';
    if (readiness < 70) return 'operational';
    if (readiness < 85) return 'embedded';
    if (readiness <= 100) return 'culture-led';
    return 'unknown';
  })();

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------
  return (
    <>
      <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
        Kincode Culture Index
      <IconHoverText
        text="How Kincode measures your culture readiness across systems, behaviors, and sentiment."
        icon={
          <UnifiedIcon
            name="Info"
            library="phosphor"
            className="w-4 h-4 text-gray-400"
          />
        }
        width={4}
        height={4}
      />
    </h2>
    
    <div className="mt-4 bg-white rounded-kcWidgets shadow-sm border border-gray-100 p-6">
      {isLoading ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-pulse">
          <div className="lg:col-span-1 flex flex-col items-center">
            <div className="h-44 w-44 rounded-full bg-gray-100" />
            <div className="mt-3 h-3 w-32 rounded-full bg-gray-100" />
          </div>
          <div className="lg:col-span-2 space-y-4">
            <div className="h-4 w-40 rounded-full bg-gray-100" />
            <div className="h-6 w-full rounded-md bg-gray-100" />
            <div className="h-20 w-full rounded-xl bg-gray-50" />
            <div className="flex items-center justify-between">
              <div className="h-4 w-40 rounded-full bg-gray-100" />
              <div className="h-3 w-32 rounded-full bg-gray-100" />
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1">
              <RingGauge percent={readiness ?? 0} colorHex={colorHex} />
              <div className="mt-3 text-center text-xs text-gray-500">
                Kincode Culture Index
              </div>
            </div>

            {/* AI summary + maturity stage (KAI message) */}
            <div className="lg:col-span-2">
              <div className="rounded-kc border border-slate-200 border-l-4 border-l-violet-500 bg-gradient-to-r from-slate-50 via-white to-slate-50 px-4 py-3 text-xs shadow-sm">
                <div className="flex items-start gap-3">
                  {/* KAI avatar */}
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-violet-100 shrink-0">
                    <img
                      src="/assets/KAI_profile_pic.png"
                      alt="KAI"
                      className="h-6 w-6"
                      loading="lazy"
                    />
                  </div>

                  {/* KAI message */}
                  <div className="space-y-1">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-violet-600 opacity-90">
                      {t('trust-center.kci.aiSummary.title')}
                    </div>

                    {/* Stage + description (always visible once main metrics are loaded) */}
                    {!isLoading && (
                      <div className="border-b border-gray-100 pb-2">
                        <div className="text-kc-black text-base">
                          <span className="">
                            {t('trust-center.kci.aiSummary.stage-label')}:{' '}
                          </span>
                          <span className="text-kc-black font-bold">
                            {t(`trust-center.kci.aiSummary.stage.${stageKey}.name`)}
                          </span>
                        </div>
                        <div className="text-gray-500 text-sm">
                          {t(`trust-center.kci.aiSummary.stage.${stageKey}.description`)}
                        </div>
                      </div>
                    )}

                    {/* Loading state for KAI summary */}
                    {isLoading && (
                      <div className="space-y-1 animate-pulse">
                        <div className="h-3 w-44 rounded-full bg-slate-200" />
                        <div className="h-3 w-56 rounded-full bg-slate-100" />
                        <div className="h-3 w-40 rounded-full bg-slate-100" />
                      </div>
                    )}

                    {/* If backend provided a KAI-generated statement, show it as the main message */}
                    {!isLoading && cultureStatement && (
                      <div className="text-gray-700 whitespace-pre-line">
                        {cultureStatement}
                      </div>
                    )}

                    {isLoading && !cultureStatement && (
                          <div className="text-gray-400">
                            {t('trust-center.kci.aiSummary.placeholder')}
                          </div>
                        )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Kincode Culture Index breakdown */}
          <div className="mt-6 border-t border-gray-100 pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
              {/* Infrastructure */}
              <div className="space-y-2 px-6 text-left">
                <div className="font-semibold text-gray-800">Infrastructure</div>
                <p className="text-gray-500 min-h-[2.75rem]">
                  {t('trust-center.kci.infrastructure.description')}
                </p>
                {infraPercent != null && (
                  <div className="mt-2 space-y-1.5">
                    <div className="flex items-center justify-between text-[11px] text-gray-500">
                      <span>{t('trust-center.kci.score-label', 'Score')}</span>
                      <span className="font-medium text-gray-700">{infraPercent}%</span>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
                      <div
                        className="h-1.5 rounded-full"
                        style={{
                          width: `${infraPercent}%`,
                          backgroundColor: colorHex || '#14b8a6',
                        }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-[11px] text-gray-500">
                      <span>{t('trust-center.kci.weight-label')}</span>
                      <span className="font-medium text-gray-700">
                        {t('trust-center.kci.weight-value', { percent: infraWeight })}
                      </span>
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setShowInfraDetails((v) => !v)}
                  className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium"
                  style={{ color: colorHex || '#14b8a6' }}
                >
                  <span>
                    {showInfraDetails
                      ? t('trust-center.kci.hide-details')
                      : t('trust-center.kci.learn-more')}
                  </span>
                  <UnifiedIcon
                    name={showInfraDetails ? 'CaretUp' : 'CaretDown'}
                    library="phosphor"
                    className="w-3 h-3"
                  />
                </button>
                {showInfraDetails && (
                  <div className="mt-2 space-y-1.5">
                    {(modules.length > 0
                      ? modules
                      : [
                          { id: 'pulse', label: 'Pulse', isActive: false },
                          { id: 'recognition', label: 'Recognition', isActive: false },
                        ]
                    ).map((m) => (
                      <div key={m.id || m.label} className="flex items-center gap-2 text-gray-700">
                        <UnifiedIcon
                          name={m.isActive ? 'CheckCircle' : 'Circle'}
                          library="phosphor"
                          className={`h-3.5 w-3.5 ${
                            m.isActive ? 'text-emerald-500' : 'text-gray-300'
                          }`}
                        />
                        <span>
                          {m.id && MODULE_COPY[m.id]?.infra
                            ? MODULE_COPY[m.id].infra
                            : m.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Adoption */}
              <div className="space-y-2 px-6 text-left">
                <div className="font-semibold text-gray-800">{t('trust-center.kci.adoption.title')}</div>
                <p className="text-gray-500 min-h-[2.75rem]">
                  {t('trust-center.kci.adoption.description')}
                </p>
                {adoptionPercent != null && (
                  <div className="mt-2 space-y-1.5">
                    <div className="flex items-center justify-between text-[11px] text-gray-500">
                      <span>{t('trust-center.kci.score-label', 'Score')}</span>
                      <span className="font-medium text-gray-700">{adoptionPercent}%</span>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
                      <div
                        className="h-1.5 rounded-full"
                        style={{
                          width: `${adoptionPercent}%`,
                          backgroundColor: colorHex || '#14b8a6',
                        }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-[11px] text-gray-500">
                      <span>{t('trust-center.kci.weight-label')}</span>
                      <span className="font-medium text-gray-700">
                        {t('trust-center.kci.weight-value', { percent: adoptionWeight })}
                      </span>
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setShowAdoptionDetails((v) => !v)}
                  className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium"
                  style={{ color: colorHex || '#14b8a6' }}
                >
                  <span>
                    {showAdoptionDetails
                      ? t('trust-center.kci.hide-details')
                      : t('trust-center.kci.learn-more')}
                  </span>
                  <UnifiedIcon
                    name={showAdoptionDetails ? 'CaretUp' : 'CaretDown'}
                    library="phosphor"
                    className="w-3 h-3"
                  />
                </button>
                {showAdoptionDetails && (
                  <div className="mt-2 space-y-1.5">
                    {(modules.length > 0
                      ? modules
                      : [
                          { id: 'pulse', label: 'Pulse', isAdopted: false },
                          { id: 'recognition', label: 'Recognition', isAdopted: false },
                        ]
                    ).map((m) => (
                      <div key={m.id || m.label} className="flex items-center gap-2 text-gray-700">
                        <UnifiedIcon
                          name={m.isAdopted ? 'CheckCircle' : 'Circle'}
                          library="phosphor"
                          className={`h-3.5 w-3.5 ${
                            m.isAdopted ? 'text-emerald-500' : 'text-gray-300'
                          }`}
                        />
                        <span>
                          {m.id && MODULE_COPY[m.id]?.adoption
                            ? MODULE_COPY[m.id].adoption
                            : m.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Engagement */}
              <div className="space-y-2 px-6 text-left">
                <div className="font-semibold text-gray-800">Engagement</div>
                <p className="text-gray-500 min-h-[2.75rem]">
                  How people feel about their work, teams, and leaders.
                </p>
                {engagementPercent != null && (
                  <div className="mt-2 space-y-1.5">
                    <div className="flex items-center justify-between text-[11px] text-gray-500">
                      <span>{t('trust-center.kci.score-label', 'Score')}</span>
                      <span className="font-medium text-gray-700">{engagementPercent}%</span>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
                      <div
                        className="h-1.5 rounded-full"
                        style={{
                          width: `${engagementPercent}%`,
                          backgroundColor: colorHex || '#14b8a6',
                        }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-[11px] text-gray-500">
                      <span>{t('trust-center.kci.weight-label')}</span>
                      <span className="font-medium text-gray-700">
                        {t('trust-center.kci.weight-value', { percent: engagementWeight })}
                      </span>
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setShowEngagementDetails((v) => !v)}
                  className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium"
                  style={{ color: colorHex || '#14b8a6' }}
                >
                  <span>
                    {showEngagementDetails
                      ? t('trust-center.kci.hide-details')
                      : t('trust-center.kci.learn-more')}
                  </span>
                  <UnifiedIcon
                    name={showEngagementDetails ? 'CaretUp' : 'CaretDown'}
                    library="phosphor"
                    className="w-3 h-3"
                  />
                </button>
                {showEngagementDetails && (
                  <div className="mt-2 space-y-1.5">
                    <div className="flex items-center gap-2 text-gray-700">
                      <UnifiedIcon
                        name={
                          typeof readiness === 'number' && readiness > 0 ? 'CheckCircle' : 'Circle'
                        }
                        library="phosphor"
                        className={`h-3.5 w-3.5 ${
                          typeof readiness === 'number' && readiness > 0
                            ? 'text-emerald-500'
                            : 'text-gray-300'
                        }`}
                      />
                      <span>{(t('trust-center.engagement-pulse.engagement-data'))}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

        </>
      )}
    </div>
    </>
  );
}

